// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminSecurity.js
// Admin: Security - who is probing the site and what the gate did about it.
// Tabs: Overview (counts, top anonymous IPs), Events (the security log, filterable), Bans (IP or
// account, temporary or permanent, lift, history), Lists (allow / deny rules for IPs, email
// domains, addresses and banned words, a test box, whitelist-only registration), Rules (auto-ban
// thresholds, lockdown settings, event retention), Accounts (lockdown / strict known-IPs mode, known IPs,
// approvals, ban, log out everywhere). API: backend/security_admin.py; enforcement: backend/security.py
// (its cache reloads within 15 s of a change made here).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';
import './AdminSecurity.css';

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '32px 16px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '6px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' },
  tabs:    { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' },
  tab:     { padding: '6px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer',
             fontSize: '13px', background: 'var(--surface)', color: 'var(--text)' },
  tabOn:   { background: 'var(--accent)', color: 'var(--on-accent)', borderColor: 'var(--accent)' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)',
             padding: '16px 18px', marginBottom: '16px' },
  title:   { fontSize: '14px', fontWeight: 600, marginBottom: '10px' },
  row:     { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)',
             fontWeight: 500, fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.05em',
             textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td:      { padding: '7px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  mono:    { fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' },
  link:    { color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', background: 'none', border: 'none',
             padding: 0, font: 'inherit', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' },
  btn:     { padding: '5px 11px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '12px',
             fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif" },
  input:   { padding: '6px 9px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '13px',
             fontFamily: "'Space Grotesk', sans-serif", background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
  select:  { padding: '6px 8px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
             background: 'var(--bg)', color: 'var(--text)' },
  tile:    { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px',
             minWidth: '110px' },
  tileN:   { fontSize: '20px', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" },
  tileL:   { fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  badge:   { display: 'inline-block', padding: '1px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 500,
             fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.04em' },
  meta:    { color: 'var(--text-muted)', fontSize: '12px' },
  err:     { color: 'var(--error)', fontSize: '13px', marginBottom: '10px' },
  ok:      { color: 'var(--success)', fontSize: '13px', marginBottom: '10px' },
  wrap:    { overflowX: 'auto' },
  detail:  { maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};

const KIND_COLORS = {
  login_fail: 'var(--error)', probe: 'var(--error)', autoban: 'var(--error)', banned_hit: 'var(--warning)',
  forbidden: 'var(--warning)', unauthorized: 'var(--text-muted)', not_found: 'var(--text-muted)', register_blocked: 'var(--warning)',
  captcha_fail: 'var(--warning)', login_new_ip: 'var(--accent)', ban_added: 'var(--accent)', ban_lifted: 'var(--success)',
  lockdown: 'var(--error)', login_blocked_ip: 'var(--error)', ip_approved: 'var(--success)', account_update: 'var(--accent)',
};
const DURATIONS = [['60', '1 hour'], ['360', '6 hours'], ['1440', '24 hours'], ['10080', '7 days'], ['43200', '30 days'],
                   ['', 'Permanent'], ['custom', 'Custom (minutes)']];

const when = t => t ? new Date(t).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const errOf = (e, d) => e?.response?.data?.error || d;

// DNS is display-only. Resolve visible IPs in batches; unanswered PTR lookups leave the IP unchanged.
function useHostnames(ips) {
  const key = [...new Set(ips.filter(ip => ip && !ip.includes('/')))].sort().join('|');
  const [hosts, setHosts] = useState({});
  useEffect(() => {
    if (!key) return;
    const addresses = key.split('|');
    let cancelled = false;
    let timer;
    let attempt = 0;
    const load = () => {
      Promise.all(Array.from({ length: Math.ceil(addresses.length / 100) }, (_, i) =>
        api.post('/admin/security/hostnames', { ips: addresses.slice(i * 100, (i + 1) * 100) })))
        .then(results => {
          if (cancelled) return;
          setHosts(old => Object.assign({}, old, ...results.map(r => r.data.hostnames)));
          if (results.some(r => r.data.pending) && attempt++ < 8) timer = setTimeout(load, 1200);
        }).catch(() => {});
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key]);
  return hosts;
}

function IpLabel({ ip, hosts = {}, onClick, full = false }) {
  if (!ip) return '—';
  const hostname = hosts[ip];
  return <span className={full ? 'security-ip-label security-ip-full' : 'security-ip-label'}>
    {onClick ? <button className="security-ip-link" style={s.link} title={hostname || ip} onClick={onClick}>{hostname || ip}</button>
      : <span className="security-ip-text" style={s.mono} title={hostname || ip}>{hostname || ip}</span>}
    {hostname && <span className="security-ip-raw" style={{ ...s.meta, ...s.mono }}>{ip}</span>}
  </span>;
}

function Btn({ onClick, color = 'var(--accent)', textColor = 'var(--on-accent)', children, disabled, small }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...s.btn, background: color, color: textColor, opacity: disabled ? 0.5 : 1, ...(small ? { padding: '3px 8px', fontSize: '11px' } : {}) }}>
      {children}
    </button>
  );
}
const Dim = ({ onClick, children, small, danger }) => (
  <Btn onClick={onClick} small={small} color={danger ? 'var(--error-dim)' : 'var(--accent-dim)'}
    textColor={danger ? 'var(--error)' : 'var(--accent)'}>{children}</Btn>
);
const Kind = ({ k }) => <span style={{ ...s.badge, background: 'var(--border)', color: KIND_COLORS[k] || 'var(--text)' }}>{k}</span>;
const Tag = ({ children, color = 'var(--text-muted)' }) => <span style={{ ...s.badge, background: 'var(--border)', color }}>{children}</span>;

// ---------------------------------------------------------------------------
export default function AdminSecurity() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const setTab = t => setParams({ tab: t });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const flash = (m, bad = false) => { bad ? setErr(m) : setMsg(m); setTimeout(() => { setMsg(''); setErr(''); }, 5000); };

  // shared across tabs: a ban form prefill and an IP being inspected
  const [banInit, setBanInit] = useState(null);
  const [ipView, setIpView] = useState(null);
  const [eventFilter, setEventFilter] = useState({ hours: '24', kind: '', ip: '', user: '', path: '', anon: false });

  useEffect(() => { if (user && !user.is_admin) navigate('/dashboard', { replace: true }); }, [user, navigate]);

  const banIp = ip => { setBanInit({ scope: 'ip', ip }); setTab('bans'); };
  const banAccount = (login) => { setBanInit({ scope: 'account', login }); setTab('bans'); };
  const showEvents = ip => { setEventFilter(f => ({ ...f, ip, hours: '168' })); setTab('events'); };

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <div style={s.heading}>Security</div>
        <div style={s.sub}>Probes, failed logins and blocked requests; IP and account bans; allow / deny lists; auto-ban rules.
          Changes apply within 15 seconds.{' '}
          <button style={s.link} onClick={() => navigate('/admin')}>Back to Admin</button></div>
        <div style={s.tabs}>
          {[['overview', 'Overview'], ['events', 'Events'], ['bans', 'Bans'], ['lists', 'Lists'], ['rules', 'Rules'], ['accounts', 'Accounts']].map(([k, l]) => (
            <button key={k} style={{ ...s.tab, ...(tab === k ? s.tabOn : {}) }} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
        {err && <div style={s.err}>{err}</div>}
        {msg && <div style={s.ok}>{msg}</div>}
        {ipView && <IpPanel ip={ipView} onClose={() => setIpView(null)} onBan={banIp} onEvents={showEvents} />}
        {tab === 'overview' && <Overview onIp={setIpView} onBan={banIp} onEvents={showEvents} flash={flash} />}
        {tab === 'events' && <Events filter={eventFilter} setFilter={setEventFilter} onIp={setIpView} onBan={banIp} flash={flash} />}
        {tab === 'bans' && <Bans init={banInit} clearInit={() => setBanInit(null)} onIp={setIpView} flash={flash} />}
        {tab === 'lists' && <Lists flash={flash} />}
        {tab === 'rules' && <Rules flash={flash} />}
        {tab === 'accounts' && <Accounts onBan={banAccount} onIp={setIpView} flash={flash} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Overview({ onIp, onBan, onEvents, flash }) {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState('24');
  useEffect(() => {
    api.get(`/admin/security/overview?hours=${hours}`).then(r => setData(r.data)).catch(e => flash(errOf(e, 'Could not load the overview'), true));
  }, [hours]); // eslint-disable-line react-hooks/exhaustive-deps
  const hosts = useHostnames(data ? [...data.top_ips.map(t => t.ip), ...data.recent_bans.map(b => b.ip)] : []);
  if (!data) return <div style={s.meta}>Loading…</div>;
  const c = data.counts || {};
  const tiles = [['probe', 'Probes'], ['login_fail', 'Failed logins'], ['not_found', 'API 404s'], ['forbidden', '403s'],
                 ['unauthorized', '401s'], ['register_blocked', 'Blocked sign-ups'], ['autoban', 'Auto-bans'], ['banned_hit', 'Banned hits']];
  return (
    <>
      <div style={s.section}>
        <div style={{ ...s.row, justifyContent: 'space-between' }}>
          <div style={s.title}>Last {hours === '1' ? 'hour' : hours === '24' ? '24 hours' : hours === '168' ? '7 days' : '30 days'}</div>
          <select style={s.select} value={hours} onChange={e => setHours(e.target.value)}>
            <option value="1">1 hour</option><option value="24">24 hours</option><option value="168">7 days</option><option value="720">30 days</option>
          </select>
        </div>
        <div style={{ ...s.row, marginTop: '10px' }}>
          {tiles.map(([k, l]) => (
            <div key={k} style={s.tile}><div style={{ ...s.tileN, color: c[k] ? KIND_COLORS[k] : 'var(--text-muted)' }}>{c[k] || 0}</div><div style={s.tileL}>{l}</div></div>
          ))}
          <div style={s.tile}><div style={s.tileN}>{(data.bans_active.ip || 0) + (data.bans_active.account || 0)}</div><div style={s.tileL}>Active bans</div></div>
        </div>
        <div style={{ ...s.meta, marginTop: '10px' }}>
          {data.events_total.n} events stored since {when(data.events_total.oldest)}; kept {data.settings.sec_events_retention_days} days.
          {data.settings.sec_register_whitelist_only === '1' && <> Registration is <b>whitelist-only</b>.</>}
        </div>
      </div>

      <div style={s.section}>
        <div style={s.title}>Top anonymous IPs</div>
        {data.top_ips.length === 0 && <div style={s.meta}>Nothing anonymous logged in this window.</div>}
        {data.top_ips.length > 0 && (
          <div className="security-top-table" style={s.wrap}><table style={s.table}>
            <thead><tr><th style={s.th}>IP</th><th style={s.th}>Events</th><th style={s.th}>Kinds</th><th style={s.th}>Last</th><th style={s.th}>Status</th><th style={s.th}></th></tr></thead>
            <tbody>{data.top_ips.map(t => (
              <tr key={t.ip}>
                <td style={s.td}><IpLabel ip={t.ip} hosts={hosts} onClick={() => onIp(t.ip)} /></td>
                <td style={s.td}>{t.n}</td>
                <td style={{ ...s.td, fontSize: '11px' }}>{t.kind_list}</td>
                <td style={s.td}>{when(t.last)}</td>
                <td style={s.td}>{t.banned ? <Tag color="var(--error)">banned</Tag> : t.exempt ? <Tag color="var(--success)">exempt</Tag> : ''}</td>
                <td style={s.td}><div style={s.row}>
                  <Dim small onClick={() => onEvents(t.ip)}>Events</Dim>
                  {!t.banned && <Dim small danger onClick={() => onBan(t.ip)}>Ban</Dim>}
                </div></td>
              </tr>))}
            </tbody>
          </table></div>
        )}
        {data.top_ips.length > 0 && <div className="security-top-cards">
          {data.top_ips.map(t => <div className="security-top-card" key={t.ip}>
            <div className="security-top-card-head">
              <IpLabel ip={t.ip} hosts={hosts} onClick={() => onIp(t.ip)} full />
              <div className="security-top-card-count"><b>{t.n}</b> events</div>
            </div>
            <div style={{ ...s.meta, marginTop: '6px', overflowWrap: 'anywhere' }}>{t.kind_list}</div>
            <div className="security-top-card-foot">
              <span style={s.meta}>{when(t.last)}{' '}
                {t.banned ? <Tag color="var(--error)">banned</Tag> : t.exempt ? <Tag color="var(--success)">exempt</Tag> : null}
              </span>
              <span style={{ ...s.row, gap: '6px' }}>
                <Dim small onClick={() => onEvents(t.ip)}>Events</Dim>
                {!t.banned && <Dim small danger onClick={() => onBan(t.ip)}>Ban</Dim>}
              </span>
            </div>
          </div>)}
        </div>}
      </div>

      <div style={s.section}>
        <div style={s.title}>Latest bans</div>
        <BanTable bans={data.recent_bans} onIp={onIp} hosts={hosts} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
function Events({ filter, setFilter, onIp, onBan, flash }) {
  const [rows, setRows] = useState([]);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [kinds, setKinds] = useState([]);
  useEffect(() => { api.get('/admin/security/overview?hours=1').then(r => setKinds(r.data.kinds)).catch(() => {}); }, []);

  const load = useCallback((append = false) => {
    setBusy(true);
    const q = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v !== '' && v !== false) q.set(k, v === true ? '1' : v); });
    if (append && rows.length) q.set('before', rows[rows.length - 1].id);
    api.get(`/admin/security/events?${q.toString()}`)
      .then(r => { setRows(append ? [...rows, ...r.data.events] : r.data.events); setMore(r.data.more); })
      .catch(e => flash(errOf(e, 'Could not load the events'), true))
      .finally(() => setBusy(false));
  }, [filter, rows]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const hosts = useHostnames(rows.map(e => e.ip));

  const f = (k, v) => setFilter(x => ({ ...x, [k]: v }));
  return (
    <div style={s.section}>
      <div style={s.row}>
        <select style={s.select} value={filter.hours} onChange={e => f('hours', e.target.value)}>
          <option value="1">Last hour</option><option value="24">24 hours</option><option value="168">7 days</option>
          <option value="720">30 days</option><option value="0">All</option>
        </select>
        <select style={s.select} value={filter.kind} onChange={e => f('kind', e.target.value)}>
          <option value="">All kinds</option>{kinds.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <label style={{ ...s.row, fontSize: '12px', gap: '4px' }}><input type="checkbox" checked={filter.anon} onChange={e => f('anon', e.target.checked)} /> anonymous only</label>
        <IpFilter value={filter.ip} onChange={v => f('ip', v)} />
        <TextFilter placeholder="user / email" value={filter.user} onChange={v => f('user', v)} />
        <TextFilter placeholder="path or detail contains" value={filter.path} onChange={v => f('path', v)} />
        <Dim small onClick={() => load(false)}>Refresh</Dim>
      </div>
      <div style={{ ...s.wrap, marginTop: '10px' }}><table style={s.table}>
        <thead><tr><th style={s.th}>Time</th><th style={s.th}>IP</th><th style={s.th}>User</th><th style={s.th}>Kind</th><th style={s.th}>St</th><th style={s.th}>Request</th><th style={s.th}>Detail</th><th style={s.th}></th></tr></thead>
        <tbody>{rows.map(e => (
          <tr key={e.id}>
            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{when(e.ts)}</td>
            <td style={s.td}><IpLabel ip={e.ip} hosts={hosts} onClick={e.ip ? () => onIp(e.ip) : null} /></td>
            <td style={s.td}>{e.username || (e.user_id ? `#${e.user_id}` : '')}{e.target ? <span style={s.meta}> → {e.target}</span> : ''}</td>
            <td style={s.td}><Kind k={e.kind} /></td>
            <td style={s.td}>{e.status || ''}</td>
            <td style={{ ...s.td, ...s.mono, maxWidth: '260px', overflowWrap: 'anywhere' }}>{e.method} {e.path}</td>
            <td style={{ ...s.td, ...s.detail }} title={`${e.detail || ''}\n${e.user_agent || ''}`}>{e.detail || ''}</td>
            <td style={s.td}>{e.ip && !e.user_id && <Dim small danger onClick={() => onBan(e.ip)}>Ban</Dim>}</td>
          </tr>))}
        </tbody>
      </table></div>
      {rows.length === 0 && !busy && <div style={{ ...s.meta, marginTop: '8px' }}>No events match.</div>}
      {more && <div style={{ marginTop: '10px' }}><Dim onClick={() => load(true)} small>{busy ? 'Loading…' : 'Load more'}</Dim></div>}
    </div>
  );
}

// Text filters commit on Enter or blur so typing doesn't reload on every key.
function TextFilter({ value, onChange, placeholder }) {
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);
  return <input style={{ ...s.input, width: '170px' }} placeholder={placeholder} value={v} onChange={e => setV(e.target.value)}
    onBlur={() => v !== value && onChange(v)} onKeyDown={e => e.key === 'Enter' && onChange(v)} />;
}
const IpFilter = p => <TextFilter {...p} placeholder="ip or range" />;

// ---------------------------------------------------------------------------
function BanTable({ bans, onLift, onIp, hosts }) {
  if (!bans || bans.length === 0) return <div style={s.meta}>No bans.</div>;
  return (
    <div style={s.wrap}><table style={s.table}>
      <thead><tr><th style={s.th}>Target</th><th style={s.th}>Mode</th><th style={s.th}>Reason</th><th style={s.th}>Source</th><th style={s.th}>From</th><th style={s.th}>Until</th><th style={s.th}>Status</th><th style={s.th}></th></tr></thead>
      <tbody>{bans.map(b => (
        <tr key={b.id}>
          <td style={s.td}>{b.scope === 'ip'
            ? <IpLabel ip={b.ip} hosts={hosts} onClick={onIp ? () => onIp(b.ip.split('/')[0]) : null} />
            : <span>{b.username || b.email || `#${b.user_id}`} <Tag>account</Tag></span>}</td>
          <td style={s.td}>{b.scope === 'ip' ? (b.mode === 'anon' ? <Tag color="var(--warning)">anon only</Tag> : <Tag color="var(--error)">full</Tag>) : ''}</td>
          <td style={{ ...s.td, maxWidth: '260px' }}>{b.reason || '—'}{b.notes ? <div style={s.meta}>{b.notes}</div> : null}</td>
          <td style={{ ...s.td, fontSize: '11px' }}>{b.source}{b.created_by ? ` (${b.created_by})` : ''}</td>
          <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{when(b.created_at)}</td>
          <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{b.expires_at ? when(b.expires_at) : 'permanent'}</td>
          <td style={s.td}>{b.lifted_at ? <Tag color="var(--success)">lifted {b.lifted_by ? `by ${b.lifted_by}` : ''}</Tag>
            : b.active ? <Tag color="var(--error)">active</Tag> : <Tag>expired</Tag>}</td>
          <td style={s.td}>{b.active && onLift && <Dim small onClick={() => onLift(b.id)}>Lift</Dim>}</td>
        </tr>))}
      </tbody>
    </table></div>
  );
}

function Bans({ init, clearInit, onIp, flash }) {
  const [bans, setBans] = useState([]);
  const [all, setAll] = useState(false);
  const [form, setForm] = useState({ scope: 'ip', ip: '', login: '', mode: 'full', duration: '1440', minutes: '', reason: '', notes: '' });
  useEffect(() => { if (init) { setForm(f => ({ ...f, ...init })); clearInit(); } }, [init]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    api.get(`/admin/security/bans?all=${all ? 1 : 0}`).then(r => setBans(r.data.bans)).catch(e => flash(errOf(e, 'Could not load the bans'), true));
  }, [all]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const hosts = useHostnames(bans.filter(b => b.scope === 'ip').map(b => b.ip));

  const add = async () => {
    const minutes = form.duration === 'custom' ? form.minutes : form.duration;
    try {
      const r = await api.post('/admin/security/bans/add', { scope: form.scope, ip: form.ip, login: form.login, mode: form.mode,
        minutes: minutes === '' ? null : minutes, reason: form.reason, notes: form.notes });
      flash(r.data.message); setForm(f => ({ ...f, ip: '', login: '', reason: '', notes: '' })); load();
    } catch (e) { flash(errOf(e, 'Could not add the ban'), true); }
  };
  const lift = async id => {
    try { await api.post('/admin/security/bans/lift', { id }); flash('Ban lifted'); load(); }
    catch (e) { flash(errOf(e, 'Could not lift the ban'), true); }
  };
  const f = (k, v) => setForm(x => ({ ...x, [k]: v }));
  return (
    <>
      <div style={s.section}>
        <div style={s.title}>Add a ban</div>
        <div style={s.row}>
          <select style={s.select} value={form.scope} onChange={e => f('scope', e.target.value)}><option value="ip">IP address / range</option><option value="account">Account</option></select>
          {form.scope === 'ip'
            ? <input style={{ ...s.input, width: '180px' }} placeholder="1.2.3.4, 1.2.3.0/24 or 1.2.3.*" value={form.ip} onChange={e => f('ip', e.target.value)} />
            : <input style={{ ...s.input, width: '180px' }} placeholder="username or email" value={form.login} onChange={e => f('login', e.target.value)} />}
          {form.scope === 'ip' && (
            <select style={s.select} value={form.mode} onChange={e => f('mode', e.target.value)} title="Anonymous-only keeps accounts already logged in from this IP working (shared office / home IPs)">
              <option value="full">Block everything</option><option value="anon">Block anonymous only</option>
            </select>)}
          <select style={s.select} value={form.duration} onChange={e => f('duration', e.target.value)}>{DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          {form.duration === 'custom' && <input style={{ ...s.input, width: '90px' }} placeholder="minutes" value={form.minutes} onChange={e => f('minutes', e.target.value)} />}
          <input style={{ ...s.input, width: '200px' }} placeholder="Reason" value={form.reason} onChange={e => f('reason', e.target.value)} />
          <input style={{ ...s.input, width: '200px' }} placeholder="Notes (optional)" value={form.notes} onChange={e => f('notes', e.target.value)} />
          <Btn onClick={add} color="var(--error)">Ban</Btn>
        </div>
        <div style={{ ...s.meta, marginTop: '8px' }}>Full IP bans also go to the firewall (once the sync job is in place). Account bans end every session of the account at once and refuse its logins.</div>
      </div>
      <div style={s.section}>
        <div style={{ ...s.row, justifyContent: 'space-between' }}>
          <div style={s.title}>{all ? 'All bans (newest 500)' : 'Active bans'}</div>
          <label style={{ ...s.row, fontSize: '12px', gap: '4px' }}><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> show history</label>
        </div>
        <BanTable bans={bans} onLift={lift} onIp={onIp} hosts={hosts} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
function Lists({ flash }) {
  const [rules, setRules] = useState([]);
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ list: 'deny', kind: 'domain', pattern: '', scope: 'all', reason: '', days: '' });
  const [test, setTest] = useState('');
  const [result, setResult] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/security/lists').then(r => { setRules(r.data.rules); setSettings(r.data.settings); })
      .catch(e => flash(errOf(e, 'Could not load the lists'), true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try { const r = await api.post('/admin/security/lists/add', form); flash(r.data.message); setForm(x => ({ ...x, pattern: '', reason: '' })); load(); }
    catch (e) { flash(errOf(e, 'Could not add the rule'), true); }
  };
  const remove = async id => {
    try { await api.post('/admin/security/lists/remove', { id }); flash('Rule removed'); load(); }
    catch (e) { flash(errOf(e, 'Could not remove the rule'), true); }
  };
  const runTest = async () => {
    try { const r = await api.post('/admin/security/lists/test', { value: test }); setResult(r.data); }
    catch (e) { flash(errOf(e, 'Test failed'), true); }
  };
  const whitelist = async on => {
    try { await api.post('/admin/security/settings', { sec_register_whitelist_only: on }); flash(on ? 'Registration is now whitelist-only' : 'Registration open to the lists'); load(); }
    catch (e) { flash(errOf(e, 'Could not save'), true); }
  };
  const f = (k, v) => setForm(x => ({ ...x, [k]: v }));
  const hint = { ip: '1.2.3.4, 1.2.3.0/24 or 1.2.3.*', domain: 'gmail.com, *.ru, mail.??', email: 'bob@example.com or *@tempmail.*', word: 'casino' }[form.kind];
  return (
    <>
      <div style={s.section}>
        <div style={s.title}>Add a rule</div>
        <div style={s.row}>
          <select style={s.select} value={form.list} onChange={e => f('list', e.target.value)}><option value="deny">Deny</option><option value="allow">Allow</option></select>
          <select style={s.select} value={form.kind} onChange={e => f('kind', e.target.value)}>
            <option value="ip">IP / range</option><option value="domain">Email domain</option><option value="email">Email address</option><option value="word">Banned word</option>
          </select>
          <input style={{ ...s.input, width: '200px' }} placeholder={hint} value={form.pattern} onChange={e => f('pattern', e.target.value)} />
          <select style={s.select} value={form.scope} onChange={e => f('scope', e.target.value)}>
            <option value="all">{form.kind === 'ip' ? 'Every request' : 'Registration'}</option>
            <option value="register">Registration only</option>
            {form.kind === 'ip' && <option value="login">Login only</option>}
          </select>
          <input style={{ ...s.input, width: '180px' }} placeholder="Reason" value={form.reason} onChange={e => f('reason', e.target.value)} />
          <input style={{ ...s.input, width: '110px' }} placeholder="expires (days)" value={form.days} onChange={e => f('days', e.target.value)} />
          <Btn onClick={add}>Add</Btn>
        </div>
        <div style={{ ...s.meta, marginTop: '8px' }}>
          Deny rules refuse registration (email domain / address / word, also checked against usernames) or, for IPs, every request, logins or registrations.
          Allow-listed IPs are never auto-banned; allow email / domain rules are who may register in whitelist-only mode.
          Wildcards: <span style={s.mono}>*</span> any run of characters, <span style={s.mono}>?</span> one character; matching ignores case.
        </div>
        <label style={{ ...s.row, fontSize: '13px', gap: '6px', marginTop: '10px' }}>
          <input type="checkbox" checked={settings.sec_register_whitelist_only === '1'} onChange={e => whitelist(e.target.checked)} />
          Whitelist-only registration: only addresses matching an allow email / domain rule may register
        </label>
      </div>

      <div style={s.section}>
        <div style={s.title}>Test an IP, email address or username</div>
        <div style={s.row}>
          <input style={{ ...s.input, width: '260px' }} placeholder="1.2.3.4 or bob@example.com or bob" value={test} onChange={e => setTest(e.target.value)} onKeyDown={e => e.key === 'Enter' && runTest()} />
          <Dim onClick={runTest}>Test</Dim>
        </div>
        {result && (
          <div style={{ marginTop: '10px', fontSize: '13px' }}>
            {result.verdict.map((v, i) => <div key={i}>• {v}</div>)}
            {result.matches.length > 0 && <div style={{ ...s.meta, marginTop: '6px' }}>Matching rules: {result.matches.map(m => `${m.list} ${m.kind} ${m.pattern} (${m.scope})`).join('; ')}</div>}
            {result.matches.length === 0 && <div style={s.meta}>No list rule matches.</div>}
            {result.accounts && result.accounts.length > 0 && <div style={s.meta}>Accounts seen from this IP: {result.accounts.map(a => a.username).join(', ')}</div>}
          </div>)}
      </div>

      <div style={s.section}>
        <div style={s.title}>Rules ({rules.length})</div>
        {rules.length === 0 && <div style={s.meta}>No rules yet.</div>}
        {rules.length > 0 && (
          <div style={s.wrap}><table style={s.table}>
            <thead><tr><th style={s.th}>List</th><th style={s.th}>Kind</th><th style={s.th}>Pattern</th><th style={s.th}>Scope</th><th style={s.th}>Reason</th><th style={s.th}>Added</th><th style={s.th}>Expires</th><th style={s.th}></th></tr></thead>
            <tbody>{rules.map(r => (
              <tr key={r.id}>
                <td style={s.td}><Tag color={r.list === 'deny' ? 'var(--error)' : 'var(--success)'}>{r.list}</Tag></td>
                <td style={s.td}>{r.kind}</td>
                <td style={{ ...s.td, ...s.mono }}>{r.pattern}</td>
                <td style={s.td}>{r.scope}</td>
                <td style={{ ...s.td, maxWidth: '240px' }}>{r.reason || '—'}</td>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{when(r.created_at)}{r.created_by ? <span style={s.meta}> {r.created_by}</span> : ''}</td>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{r.expires_at ? when(r.expires_at) : '—'}</td>
                <td style={s.td}><Dim small danger onClick={() => remove(r.id)}>Remove</Dim></td>
              </tr>))}
            </tbody>
          </table></div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
function Rules({ flash }) {
  const [rules, setRules] = useState([]);
  const [retention, setRetention] = useState('');
  const [lock, setLock] = useState({ sec_lockdown_fails: '', sec_lockdown_ips: '', sec_lockdown_window_min: '', sec_lockdown_hours: '' });
  const load = useCallback(() => {
    api.get('/admin/security/auto').then(r => setRules(r.data.rules)).catch(e => flash(errOf(e, 'Could not load the rules'), true));
    api.get('/admin/security/overview?hours=1').then(r => {
      setRetention(r.data.settings.sec_events_retention_days);
      setLock(Object.fromEntries(Object.keys(lock).map(k => [k, r.data.settings[k]])));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const saveLock = async () => {
    try { await api.post('/admin/security/settings', lock); flash('Lockdown settings saved'); }
    catch (e) { flash(errOf(e, 'Could not save'), true); }
  };
  useEffect(() => { load(); }, [load]);
  const edit = (id, k, v) => setRules(rs => rs.map(r => r.id === id ? { ...r, [k]: v } : r));
  const save = async r => {
    try { await api.post('/admin/security/auto/update', r); flash(`Saved ${r.name}`); }
    catch (e) { flash(errOf(e, 'Could not save the rule'), true); }
  };
  const saveRetention = async () => {
    try { await api.post('/admin/security/settings', { sec_events_retention_days: retention }); flash('Retention saved'); }
    catch (e) { flash(errOf(e, 'Could not save'), true); }
  };
  const num = { ...s.input, width: '70px', textAlign: 'right' };
  return (
    <>
      <div style={s.section}>
        <div style={s.title}>Auto-ban rules</div>
        <div style={{ ...s.meta, marginBottom: '10px' }}>
          Threshold anonymous events of a kind from one IP inside the window → IP ban for the minutes shown (empty = permanent).
          Escalate doubles the duration for every earlier auto-ban of that IP in the last 30 days (capped at 30 days).
          IPs that several accounts log in from get an anonymous-only ban; allow-listed IPs and your own recent login IPs are never auto-banned.
        </div>
        <div style={s.wrap}><table style={s.table}>
          <thead><tr><th style={s.th}>Rule</th><th style={s.th}>Event</th><th style={s.th}>Threshold</th><th style={s.th}>Window (min)</th><th style={s.th}>Ban (min)</th><th style={s.th}>Escalate</th><th style={s.th}>Discord</th><th style={s.th}>Enabled</th><th style={s.th}></th></tr></thead>
          <tbody>{rules.map(r => (
            <tr key={r.id}>
              <td style={s.td}>{r.name}</td>
              <td style={s.td}><Kind k={r.event_kind} /></td>
              <td style={s.td}><input style={num} value={r.threshold} onChange={e => edit(r.id, 'threshold', e.target.value)} /></td>
              <td style={s.td}><input style={num} value={r.window_min} onChange={e => edit(r.id, 'window_min', e.target.value)} /></td>
              <td style={s.td}><input style={num} value={r.ban_minutes ?? ''} placeholder="perm" onChange={e => edit(r.id, 'ban_minutes', e.target.value)} /></td>
              <td style={s.td}><input type="checkbox" checked={!!r.escalate} onChange={e => edit(r.id, 'escalate', e.target.checked)} /></td>
              <td style={s.td}><input type="checkbox" checked={!!r.notify} onChange={e => edit(r.id, 'notify', e.target.checked)} /></td>
              <td style={s.td}><input type="checkbox" checked={!!r.enabled} onChange={e => edit(r.id, 'enabled', e.target.checked)} /></td>
              <td style={s.td}><Dim small onClick={() => save(r)}>Save</Dim></td>
            </tr>))}
          </tbody>
        </table></div>
      </div>
      <div style={s.section}>
        <div style={s.title}>Account lockdown</div>
        <div style={{ ...s.meta, marginBottom: '10px' }}>
          When one account collects this many failed logins from at least this many different IPs inside the window, it goes into lockdown
          for the hours shown: logins then succeed only from IPs the account has used before. A correct password from a new IP gets the
          same generic failure as a wrong one and the owner is emailed an approval link. Lift, extend or make it permanent per account on the Accounts tab.
        </div>
        <div style={s.row}>
          <input style={num} value={lock.sec_lockdown_fails} onChange={e => setLock(l => ({ ...l, sec_lockdown_fails: e.target.value }))} /> <span style={s.meta}>failed logins from</span>
          <input style={num} value={lock.sec_lockdown_ips} onChange={e => setLock(l => ({ ...l, sec_lockdown_ips: e.target.value }))} /> <span style={s.meta}>IPs within</span>
          <input style={num} value={lock.sec_lockdown_window_min} onChange={e => setLock(l => ({ ...l, sec_lockdown_window_min: e.target.value }))} /> <span style={s.meta}>minutes → lockdown for</span>
          <input style={num} value={lock.sec_lockdown_hours} onChange={e => setLock(l => ({ ...l, sec_lockdown_hours: e.target.value }))} /> <span style={s.meta}>hours</span>
          <Dim small onClick={saveLock}>Save</Dim>
        </div>
      </div>
      <div style={s.section}>
        <div style={s.title}>Event log retention</div>
        <div style={s.row}>
          <input style={num} value={retention} onChange={e => setRetention(e.target.value)} /> <span style={s.meta}>days (7–3650); bans and lists are kept regardless</span>
          <Dim small onClick={saveRetention}>Save</Dim>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
function IpPanel({ ip, onClose, onBan, onEvents }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setD(null); setError('');
    api.get(`/admin/security/ip?ip=${encodeURIComponent(ip)}`).then(r => setD(r.data)).catch(e => setError(errOf(e, 'Could not load the IP')));
  }, [ip]);
  const hosts = useHostnames([ip]);
  return (
    <div style={{ ...s.section, borderColor: 'var(--accent)' }}>
      <div style={{ ...s.row, justifyContent: 'space-between' }}>
        <div style={s.title}><IpLabel ip={ip} hosts={hosts} />
          {d?.shared && <> <Tag color="var(--warning)">shared IP</Tag></>}
          {d?.exempt && <> <Tag color="var(--success)">never auto-banned</Tag></>}</div>
        <div style={s.row}>
          <Dim small onClick={() => onEvents(ip)}>Events</Dim>
          <Dim small danger onClick={() => onBan(ip)}>Ban</Dim>
          <Dim small onClick={onClose}>Close</Dim>
        </div>
      </div>
      {error && <div style={s.err}>{error}</div>}
      {d && (
        <div style={{ fontSize: '13px' }}>
          <div><b>Last 7 days:</b> {d.kinds.length ? d.kinds.map(k => `${k.kind} ${k.n}`).join(', ') : 'no events'}</div>
          <div style={{ marginTop: '4px' }}><b>Accounts that logged in from here:</b> {d.accounts.length
            ? d.accounts.map(a => `${a.username}${a.is_admin ? ' (admin)' : ''} - ${a.logins} logins, last ${when(a.last_seen)}`).join('; ')
            : 'none'}</div>
          {d.rules.length > 0 && <div style={{ marginTop: '4px' }}><b>List rules:</b> {d.rules.map(r => `${r.list} ${r.pattern} (${r.scope})`).join('; ')}</div>}
          <div style={{ marginTop: '8px' }}><BanTable bans={d.bans} /></div>
        </div>)}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Accounts({ onBan, onIp, flash }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const search = useCallback(() => {
    api.get(`/admin/security/accounts?q=${encodeURIComponent(q)}`).then(r => setList(r.data.accounts))
      .catch(e => flash(errOf(e, 'Could not search accounts'), true));
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      {sel && <AccountPanel userId={sel} onClose={() => setSel(null)} onBan={onBan} onIp={onIp} flash={flash} onChanged={search} />}
      <div style={s.section}>
        <div style={s.row}>
          <input style={{ ...s.input, width: '240px' }} placeholder="username or email (empty = recent)" value={q}
            onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} />
          <Dim onClick={search}>Search</Dim>
        </div>
        <div style={{ ...s.wrap, marginTop: '10px' }}><table style={s.table}>
          <thead><tr><th style={s.th}>Account</th><th style={s.th}>Last login</th><th style={s.th}>Logins</th><th style={s.th}>Known IPs</th><th style={s.th}>Status</th><th style={s.th}></th></tr></thead>
          <tbody>{list.map(u => (
            <tr key={u.id}>
              <td style={s.td}>{u.username || '—'} <span style={s.meta}>{u.email}</span>{u.is_admin ? <> <Tag color="var(--accent)">admin</Tag></> : null}</td>
              <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{when(u.last_login)}</td>
              <td style={s.td}>{u.login_count}</td>
              <td style={s.td}>{u.known_ips}</td>
              <td style={s.td}>
                {u.banned && <Tag color="var(--error)">banned</Tag>} {u.locked && <Tag color="var(--error)">lockdown</Tag>} {u.strict_ips && <Tag color="var(--warning)">strict IPs</Tag>}
                {!u.confirmed && <Tag>unconfirmed</Tag>}
              </td>
              <td style={s.td}><Dim small onClick={() => setSel(u.id)}>Open</Dim></td>
            </tr>))}
          </tbody>
        </table></div>
      </div>
    </>
  );
}

function AccountPanel({ userId, onClose, onBan, onIp, flash, onChanged }) {
  const [d, setD] = useState(null);
  const [newIp, setNewIp] = useState('');
  const load = useCallback(() => {
    api.get(`/admin/security/account?user_id=${userId}`).then(r => setD(r.data)).catch(e => flash(errOf(e, 'Could not load the account'), true));
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const hosts = useHostnames(d ? [...d.known_ips.map(k => k.ip), ...d.events.map(e => e.ip), ...d.approvals.map(a => a.ip)] : []);
  const act = async (url, body, ok) => {
    try { const r = await api.post(url, { user_id: userId, ...body }); flash(r.data.message || ok); load(); onChanged && onChanged(); }
    catch (e) { flash(errOf(e, 'Failed'), true); }
  };
  if (!d) return <div style={{ ...s.section, borderColor: 'var(--accent)' }}><div style={s.meta}>Loading…</div></div>;
  const u = d.user;
  return (
    <div style={{ ...s.section, borderColor: 'var(--accent)' }}>
      <div style={{ ...s.row, justifyContent: 'space-between' }}>
        <div style={s.title}>{u.username || u.email} <span style={s.meta}>{u.email} · #{u.id}</span>
          {u.locked && <> <Tag color="var(--error)">lockdown until {when(u.lockdown_until)}</Tag></>}
          {u.strict_ips && <> <Tag color="var(--warning)">strict IPs</Tag></>}</div>
        <Dim small onClick={onClose}>Close</Dim>
      </div>
      <div style={{ ...s.row, marginTop: '6px' }}>
        {u.locked
          ? <Dim small onClick={() => act('/admin/security/account/update', { lockdown: 'lift' })}>Lift lockdown</Dim>
          : <Dim small onClick={() => act('/admin/security/account/update', { lockdown: 'set', hours: 24 })}>Lock down 24 h</Dim>}
        <Dim small onClick={() => act('/admin/security/account/update', { strict_ips: !u.strict_ips })}>{u.strict_ips ? 'Strict IPs off' : 'Strict IPs on (known IPs only, always)'}</Dim>
        <Dim small onClick={() => act('/admin/security/account/logout_all', {}, 'Logged out everywhere')}>Log out everywhere</Dim>
        <Dim small danger onClick={() => onBan(u.username || u.email)}>Ban account</Dim>
      </div>
      <div style={{ fontSize: '13px', marginTop: '10px' }}>
        <b>Known IPs</b> <span style={s.meta}>(logins succeed from these during lockdown / strict mode)</span>
        <div style={s.wrap}><table style={s.table}>
          <thead><tr><th style={s.th}>IP</th><th style={s.th}>First</th><th style={s.th}>Last</th><th style={s.th}>Logins</th><th style={s.th}>Via</th><th style={s.th}></th></tr></thead>
          <tbody>{d.known_ips.map(k => (
            <tr key={k.ip}>
              <td style={s.td}><IpLabel ip={k.ip} hosts={hosts} onClick={() => onIp(k.ip)} /></td>
              <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{when(k.first_seen)}</td>
              <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{when(k.last_seen)}</td>
              <td style={s.td}>{k.logins}</td><td style={s.td}>{k.via}</td>
              <td style={s.td}><Dim small danger onClick={() => act('/admin/security/account/known_ip', { ip: k.ip, action: 'remove' })}>Forget</Dim></td>
            </tr>))}
          </tbody>
        </table></div>
        <div style={{ ...s.row, marginTop: '6px' }}>
          <input style={{ ...s.input, width: '160px' }} placeholder="add an IP" value={newIp} onChange={e => setNewIp(e.target.value)} />
          <Dim small onClick={() => { act('/admin/security/account/known_ip', { ip: newIp, action: 'add' }); setNewIp(''); }}>Add known IP</Dim>
        </div>
        {d.approvals.length > 0 && (
          <div style={{ marginTop: '10px' }}><b>Approval links</b>
            {d.approvals.map((a, i) => <div key={i} style={s.meta}><IpLabel ip={a.ip} hosts={hosts} /> · sent {when(a.created_at)} · {a.used_at ? `used ${when(a.used_at)}` : new Date(a.expires_at) > new Date() ? 'pending' : 'expired'}</div>)}
          </div>)}
        <div style={{ marginTop: '10px' }}><b>Account bans</b><BanTable bans={d.bans} onIp={onIp} /></div>
        <div style={{ marginTop: '10px' }}><b>Recent events</b> <span style={s.meta}>(by this account, or aimed at it)</span>
          <div style={s.wrap}><table style={s.table}>
            <tbody>{d.events.map(e => (
              <tr key={e.id}>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{when(e.ts)}</td>
                <td style={s.td}><IpLabel ip={e.ip} hosts={hosts} onClick={e.ip ? () => onIp(e.ip) : null} /></td>
                <td style={s.td}><Kind k={e.kind} /></td>
                <td style={{ ...s.td, ...s.detail }} title={e.detail || ''}>{e.detail || e.path}</td>
              </tr>))}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
